/**
 * Haptic Feedback Utility
 *
 * Platform-aware haptic feedback:
 * - iOS (Capacitor): Uses native Taptic Engine via @capacitor/haptics
 * - Android (PWA): Uses Navigator.vibrate API
 * - Desktop/unsupported: No-ops gracefully
 */

import { isNativeApp } from './pwa';

// Capacitor haptics are loaded dynamically to avoid bundling in the web build.
// These are only available when running inside a native Capacitor app.
let _Haptics: typeof import('@capacitor/haptics').Haptics | null = null;
let _ImpactStyle: typeof import('@capacitor/haptics').ImpactStyle | null = null;
let _NotificationType: typeof import('@capacitor/haptics').NotificationType | null = null;

if (isNativeApp()) {
  import('@capacitor/haptics').then((mod) => {
    _Haptics = mod.Haptics;
    _ImpactStyle = mod.ImpactStyle;
    _NotificationType = mod.NotificationType;
  }).catch(() => {
    // Not in a native context — web fallback will be used
  });
}

export type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

// Web vibration fallback patterns in milliseconds
// Single number = duration, array = [vibrate, pause, vibrate, ...]
const WEB_PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 10],           // Short double tap
  warning: [25, 100, 25],          // Medium double tap
  error: [50, 100, 50, 100, 50],   // Triple buzz
  selection: 5,                     // Very light for selections
};

/**
 * Check if haptic feedback is supported on the current platform
 */
export function isHapticsSupported(): boolean {
  return isNativeApp() || 'vibrate' in navigator;
}

/**
 * Trigger native Capacitor haptic feedback (iOS Taptic Engine)
 */
function triggerNativeHaptic(pattern: HapticPattern): void {
  if (!_Haptics || !_ImpactStyle || !_NotificationType) return;
  switch (pattern) {
    case 'light':
      _Haptics.impact({ style: _ImpactStyle.Light });
      break;
    case 'medium':
      _Haptics.impact({ style: _ImpactStyle.Medium });
      break;
    case 'heavy':
      _Haptics.impact({ style: _ImpactStyle.Heavy });
      break;
    case 'success':
      _Haptics.notification({ type: _NotificationType.Success });
      break;
    case 'warning':
      _Haptics.notification({ type: _NotificationType.Warning });
      break;
    case 'error':
      _Haptics.notification({ type: _NotificationType.Error });
      break;
    case 'selection':
      _Haptics.selectionChanged();
      break;
  }
}

/**
 * Trigger web vibration fallback (Android PWA)
 */
function triggerWebHaptic(pattern: HapticPattern): void {
  if ('vibrate' in navigator) {
    navigator.vibrate(WEB_PATTERNS[pattern]);
  }
}

/**
 * Trigger haptic feedback with a predefined pattern
 *
 * Uses native Taptic Engine on iOS (via Capacitor), falls back to
 * Navigator.vibrate on Android PWA, and no-ops on unsupported platforms.
 *
 * @param pattern - The haptic pattern to use
 * @returns true if vibration was triggered, false if not supported
 *
 * @example
 * triggerHaptic('success');  // On successful action
 * triggerHaptic('light');    // On button press
 * triggerHaptic('error');    // On error
 * triggerHaptic('selection'); // On picker/toggle selection
 */
export function triggerHaptic(pattern: HapticPattern = 'light'): boolean {
  if (!isHapticsSupported()) {
    return false;
  }

  try {
    if (isNativeApp()) {
      triggerNativeHaptic(pattern);
    } else {
      triggerWebHaptic(pattern);
    }
    return true;
  } catch (error) {
    console.debug('Haptic feedback failed:', error);
    return false;
  }
}

/**
 * Trigger a custom vibration pattern (web only, no-ops on native)
 *
 * @param pattern - Custom pattern as duration (ms) or array [vibrate, pause, vibrate, ...]
 * @returns true if vibration was triggered, false if not supported
 */
export function triggerCustomHaptic(pattern: number | number[]): boolean {
  if (!('vibrate' in navigator)) {
    return false;
  }

  try {
    navigator.vibrate(pattern);
    return true;
  } catch (error) {
    console.debug('Custom haptic feedback failed:', error);
    return false;
  }
}

/**
 * Stop any ongoing vibration
 */
export function stopHaptic(): void {
  if ('vibrate' in navigator) {
    navigator.vibrate(0);
  }
}

/**
 * Higher-order function to wrap click handlers with haptic feedback
 *
 * @param handler - The original click handler
 * @param pattern - The haptic pattern to use (default: 'light')
 * @returns Wrapped handler that triggers haptics before the original handler
 *
 * @example
 * <button onClick={withHaptic(handleSubmit, 'success')}>Submit</button>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withHaptic<T extends (...args: any[]) => any>(
  handler: T,
  pattern: HapticPattern = 'light'
): (...args: Parameters<T>) => ReturnType<T> {
  return (...args: Parameters<T>): ReturnType<T> => {
    triggerHaptic(pattern);
    return handler(...args);
  };
}

